'use client';

import * as React from 'react';
import { businessDaysBetween } from '@/lib/brussels-time';
import { apiFetch } from '@/lib/api-client';

type AvailabilityBody = {
  ok: boolean;
  days: Record<string, string[]>;
  degraded: boolean;
};

/** A hung request must not leave the grid on a spinner forever. */
const REQUEST_TIMEOUT_MS = 12 * 1000;

export type DemoAvailability = {
  /** Known-good free slots per Brussels date key. A missing key means "not known yet". */
  days: Record<string, string[]>;
  /** Days whose free/busy could not be read — offer the full grid for these. */
  fallbackDays: Set<string>;
  /** Re-reads every range, e.g. after a slot turned out to be taken. */
  refresh: () => void;
  /** Removes a slot locally, so a slot just refused is gone before the refetch lands. */
  dropSlot: (date: string, time: string) => void;
};

/**
 * Reads free/busy for one visible range at a time and accumulates the answers, so
 * paging back to a month already seen costs no request. Failures are tracked per day
 * rather than globally: one bad month must not discard another month's real
 * availability, nor present its unfiltered grid as if it were free.
 */
export function useDemoAvailability(
  from: string | null,
  to: string | null,
  /** Which host's grid to read. The careers page books a different calendar through an
   *  endpoint with the same contract. */
  endpoint = '/api/cultiv/availability'
): DemoAvailability {
  const [days, setDays] = React.useState<Record<string, string[]>>({});
  const [fallbackDays, setFallbackDays] = React.useState<Set<string>>(new Set());
  const [token, setToken] = React.useState(0);
  const fetched = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!from || !to) return;
    const key = `${from}:${to}`;
    if (fetched.current.has(key)) return;
    const ctrl = new AbortController();
    /* Distinguishes the timeout's abort, which must degrade, from the cleanup abort on
       unmount or a range change, which must leave state alone. */
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, REQUEST_TIMEOUT_MS);

    /* Unreadable free/busy: mark the range's days so the caller offers the whole grid,
       and leave the range unfetched so revisiting it retries. */
    const markFallback = () => {
      const affected = businessDaysBetween(from, to);
      setFallbackDays((prev) => new Set([...prev, ...affected]));
    };

    (async () => {
      try {
        const res = await apiFetch(`${endpoint}?from=${from}&to=${to}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`availability API ${res.status}`);
        const body: AvailabilityBody = await res.json();
        if (!body.ok) throw new Error('availability unavailable');
        if (body.degraded) {
          markFallback();
          return;
        }
        fetched.current.add(key);
        setDays((prev) => ({ ...prev, ...body.days }));
        setFallbackDays((prev) => {
          if (!prev.size) return prev;
          const next = new Set(prev);
          for (const day of Object.keys(body.days)) next.delete(day);
          return next;
        });
      } catch {
        if (ctrl.signal.aborted && !timedOut) return;
        markFallback();
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [from, to, token, endpoint]);

  const refresh = React.useCallback(() => {
    fetched.current.clear();
    setToken((t) => t + 1);
  }, []);

  const dropSlot = React.useCallback((date: string, time: string) => {
    setDays((prev) => {
      const current = prev[date];
      if (!current?.includes(time)) return prev;
      return { ...prev, [date]: current.filter((slot) => slot !== time) };
    });
  }, []);

  return { days, fallbackDays, refresh, dropSlot };
}

/** The same grid, read from the interview host's calendar. */
export function useInterviewAvailability(from: string | null, to: string | null): DemoAvailability {
  return useDemoAvailability(from, to, '/api/cultiv/interview-availability');
}
